import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ArtifactType, ConversionTargetType, Format, Message } from 'src/types';
import { toast } from 'react-toastify';
import { COMMON_ERROR_TEXT } from 'src/constants';
import {
  buildFullMarkdownFromMessage,
  convertMarkdownToHTMLString,
} from 'src/v2/components/Editor/components/DownloadFileTool/utils';
import { convertFileToFormData, convertHTMLStringToFile } from 'src/utils';
import {
  useGetURLForFileUploadMutation,
  useUploadFileByURLMutation,
} from 'src/store/services';
import {
  useDownloadPermittedFile,
  usePollingFileStatus,
  useSession,
  useEditorState,
} from 'src/hooks';
import { useCallback, useMemo } from 'react';
import { getEditorMarkdown } from 'src/v2/components/Editor/utils';

const FILE_NAME = 'message_content_upload.html';

type DownloadToolProps = {
  message?: Message;
  activeTab?: string;
  taskTitle?: string;
  downloadToolType?: 'message' | 'editor';
};

export const useDownloadFileTool = ({
  message,
  activeTab,
  taskTitle,
  downloadToolType = 'message',
}: DownloadToolProps) => {
  const {
    appUser: { user_id },
  } = useSession();
  const [getFileUploadUrl, { isLoading: isPreSignedLoading }] =
    useGetURLForFileUploadMutation();
  const [uploadFileByUrl, { isLoading: isUploadLoading }] =
    useUploadFileByURLMutation();

  const { pollingFileStatus, isPollingLoading } = usePollingFileStatus();
  const { onDownloadPermittedFile } = useDownloadPermittedFile();

  const { editorType } = useEditorState();
  const [editor] = useLexicalComposerContext();

  const isLoading = isPreSignedLoading || isUploadLoading || isPollingLoading;

  const getUploadDetails = useCallback(
    async (fileType: ConversionTargetType) => {
      const bodyForGettingUrl = {
        user_id,
        filename: FILE_NAME,
        conversion_target_type: fileType,
        content_type: 'text/html',
        apiContext: { user_id },
      };

      const getUrlResult = await getFileUploadUrl(bodyForGettingUrl).unwrap();
      const url = getUrlResult.form_data.url;
      const fields = getUrlResult.form_data.fields;
      const fileId = getUrlResult.filename;

      return { url, fields, fileId };
    },
    [getFileUploadUrl, user_id],
  );

  const getHTMLStringFromMessage = useCallback(async () => {
    if (!activeTab) {
      return '';
    }

    const markdown = buildFullMarkdownFromMessage({
      message,
      activeTab,
    });

    return await convertMarkdownToHTMLString(markdown);
  }, [activeTab, message]);

  const getHTMLStringFromEditor = useCallback(async () => {
    const markdown =
      editorType === ArtifactType.TEXT ? getEditorMarkdown(editor) : '';
    return await convertMarkdownToHTMLString(markdown);
  }, [editor, editorType]);

  const uploadFile = useCallback(
    async ({
      htmlString,
      url,
      fields,
    }: {
      htmlString: string;
      url: string;
      fields: {
        [key: string]: string;
      };
    }) => {
      const file = convertHTMLStringToFile({
        htmlString,
        fileName: FILE_NAME,
      });

      const body = convertFileToFormData({
        fields,
        file,
      });

      await uploadFileByUrl({
        url,
        body,
      });
    },
    [uploadFileByUrl],
  );

  const downloadFile = useCallback(
    async (fileId: string, fileType: ConversionTargetType) => {
      const pollingResult = await pollingFileStatus({
        file_id: fileId,
        // (olha): we can assume that the types match
        format: fileType as unknown as Format,
        download_filename_override: taskTitle,
      });

      const fileUrlForDownload = pollingResult.completed[0]?.url;

      if (!fileUrlForDownload) {
        toast.dismiss();
        toast.error(COMMON_ERROR_TEXT);
        return;
      }

      onDownloadPermittedFile(fileUrlForDownload);
      toast.dismiss();
    },
    [taskTitle, onDownloadPermittedFile, pollingFileStatus],
  );

  const handleDownloadClick = useCallback(
    async ({
      fileType,
      onStart,
      onSuccess,
      onFail,
    }: {
      fileType: ConversionTargetType;
      onStart?: () => void;
      onSuccess?: () => void;
      onFail?: () => void;
    }) => {
      try {
        onStart?.();
        const { url, fields, fileId } = await getUploadDetails(fileType);
        const htmlString =
          downloadToolType === 'message'
            ? await getHTMLStringFromMessage()
            : await getHTMLStringFromEditor();

        await uploadFile({
          htmlString,
          url,
          fields,
        });

        await downloadFile(fileId, fileType);
        onSuccess?.();
      } catch (error) {
        toast.dismiss();
        toast.error(COMMON_ERROR_TEXT);
        onFail?.();
      }
    },
    [
      downloadToolType,
      downloadFile,
      uploadFile,
      getHTMLStringFromMessage,
      getUploadDetails,
      getHTMLStringFromEditor,
    ],
  );

  return useMemo(
    () => ({
      onDownload: handleDownloadClick,
      isLoading,
    }),
    [handleDownloadClick, isLoading],
  );
};
